import { Alert } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { FormComponentProvider } from 'context/FormContext';
import { IconInfoCircle, IconExclamationCircle } from '@tabler/icons-react';

import Tags from './Tags';
import Input from './Input';
import Select from './Select';
import Submit from './Submit';
import Number from './Number';
import Slider from './Slider';
import Switch from './Switch';
import UrlInput from './UrlInput';
import Checkbox from './Checkbox';
import Textarea from './Textarea';
import Timezone from './Timezone';
import PhoneInput from './PhoneInput';
import SwitchRadio from './SwitchRadio';
import Autocomplete from './Autocomplete';
import EditableText from './EditableText';
import Password, { PasswordCheck } from './Password';

/**
 * Props for the Form component
 */
interface FormProps {
  /** Error message to display above the form */
  error?: string | null;
  /** Success message to display above the form */
  success?: string | null;
  /** Form content and input components */
  children: React.ReactNode;
  /** Mantine form instance with validation and state management */
  form: UseFormReturnType<any>;
  /** Form submission handler that returns a promise */
  onSubmit: (values: any) => Promise<void>;
}

/**
 * Form - A comprehensive form wrapper component with integrated validation, error handling, and UI feedback.
 * 
 * This component provides a standardized form interface throughout the application with:
 * - Automatic error and success message display
 * - Integration with Mantine's form system for validation
 * - Consistent styling and user experience
 * - Rich set of form controls via compound component pattern
 * - Form context for nested components
 * 
 * Available form controls (accessed via Form.ComponentName):
 * - Form.Input: Text input with validation
 * - Form.Password: Password input with strength checking
 * - Form.Select: Dropdown selection component
 * - Form.Textarea: Multi-line text input
 * - Form.Checkbox: Boolean selection control
 * - Form.Switch: Toggle switch component
 * - Form.PhoneInput: International phone number input
 * - Form.UrlInput: URL validation input
 * - Form.Tags: Tag selection and creation
 * - Form.Submit: Form submission button
 * - And many more...
 * 
 * @example
 * const form = useForm({
 *   initialValues: { email: '', password: '' },
 *   validate: { email: isEmail('Invalid email') }
 * });
 * 
 * <Form form={form} onSubmit={handleSubmit} error={error}>
 *   <Form.Input name="email" label="Email" />
 *   <Form.Password name="password" label="Password" />
 *   <Form.Submit>Sign In</Form.Submit>
 * </Form>
 * 
 * @param props - Form component props
 * @returns JSX.Element
 */
const Form = ({ form, error, onSubmit, children, success }: FormProps) => {
  return (
    <FormComponentProvider form={form}>
      <form onSubmit={form.onSubmit(onSubmit)}>
        {error && (
          <Alert
            p="sm"
            mb="lg"
            color="red"
            radius="md"
            icon={<IconExclamationCircle size={20} />}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            p="sm"
            mb="lg"
            radius="md"
            color="green"
            variant="light"
            icon={<IconInfoCircle size={20} />}
          >
            {success}
          </Alert>
        )}
        {children}
      </form>
    </FormComponentProvider>
  );
};

Form.Tags = Tags;
Form.Input = Input;
Form.Select = Select;
Form.Number = Number;
Form.Submit = Submit;
Form.Slider = Slider;
Form.Switch = Switch;
Form.Password = Password;
Form.UrlInput = UrlInput;
Form.Checkbox = Checkbox;
Form.Textarea = Textarea;
Form.Timezone = Timezone;
Form.PhoneInput = PhoneInput;
Form.SwitchRadio = SwitchRadio;
Form.Autocomplete = Autocomplete;
Form.PasswordCheck = PasswordCheck;
Form.EditableText = EditableText;

export default Form;
